import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { 
  Building2, ArrowLeft, ArrowRight, Sparkles, ShieldCheck, FileCheck,
  CreditCard, Landmark, CheckCircle2, UserCheck, Briefcase, FileSpreadsheet,
  Globe, Search, Filter, Zap
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { RegionalPaymentModal } from './RegionalPaymentModal';

interface PolandCenterHubViewProps {
  onLaunchAgent: (target: string, preferredLang?: 'pl' | 'uk' | 'en') => void;
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currency?: Currency;
  currentUserEmail?: string;
  onSelectLanguage?: (lang: 'uk' | 'en' | 'pl') => void;
  onSelectCurrency?: (cur: Currency) => void;
}

export const POLAND_AGENT_IDS = [
  'ksef-vat-auditor',
  'spzoo-krs-architect',
  'construction-estimate-parser-avk',
  'zus-a1-delegation-specialist',
  'pzp-miniportal-procurement',
  'egib-geoportal-auditor',
  'cit-estonski-modeler',
  'biala-lista-vat-auditor',
  'b2b-contract-labor-auditor',
  'ceny-transferowe-tp-generator',
  'ulga-b-r-ip-box-architect',
  'uchwaly-krs-rdf-spzoo',
  'cudzoziemcy-karta-pobytu-architect',
  'rodo-gdpr-dpo-compliance-auditor',
  'upadlosc-restrukturyzacja-doradca',
  'bdo-waste-esg-auditor',
  'dotacje-ue-kpo-feng-architect',
  'mdr-tax-schemes-auditor',
  'ekw-rejestr-zastawow-auditor',
  'windykacja-epu-odsetki-generator',
  'sygnalisci-whistleblowing-auditor',
  'oze-cppa-contract-architect',
  'gitd-cemt-transport-auditor'
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Building2 className={className} />;
  return <IconComponent className={className} />;
};

export default function PolandCenterHubView({
  onLaunchAgent,
  onBack,
  language,
  currency = 'PLN',
  currentUserEmail,
  onSelectLanguage,
  onSelectCurrency
}: PolandCenterHubViewProps) {
  // Default to Polish for Poland Hub for authentic native experience, with switcher support
  const [activeLang, setActiveLang] = useState<'pl' | 'uk' | 'en'>(language || 'pl');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentAgent, setSelectedPaymentAgent] = useState<{ id: string; name: string; price: number; singlePrice: number } | null>(null);

  const handleOpenPayment = (id: string, name: string, price: number, singlePrice: number) => {
    setSelectedPaymentAgent({ id, name, price, singlePrice });
    setPaymentModalOpen(true);
  };

  useEffect(() => {
    if (language) setActiveLang(language);
  }, [language]);

  const isUk = activeLang === 'uk';
  const isPl = activeLang === 'pl';
  const isEn = activeLang === 'en';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }>>({});

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('Failed to fetch Poland pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  const agents = agentsConfig.filter((a: any) => POLAND_AGENT_IDS.includes(a.id));

  const categories = [
    { id: 'all', label: isPl ? 'Wszystkie moduły PL (23)' : isUk ? 'Усі модулі PL (23)' : 'All PL Modules (23)' },
    { id: 'tax', label: isPl ? 'Podatki, KSeF & EPU' : isUk ? 'Податки, KSeF & EPU' : 'Taxes, KSeF & EPU' },
    { id: 'corporate', label: isPl ? 'Spółki, KRS & Upadłość' : isUk ? 'ТОВ, KRS & Банкрутство' : 'Corporate & Restructuring' },
    { id: 'hr', label: isPl ? 'Sygnaliści, RODO & ZUS' : isUk ? 'Сигналісти, RODO & ZUS' : 'Whistleblowing & HR' },
    { id: 'energy_trans', label: isPl ? 'OZE cPPA & Transport GITD' : isUk ? 'ВДЕ cPPA & Транспорт GITD' : 'Renewables & Transport' },
    { id: 'grants', label: isPl ? 'Dotacje KPO & PZP' : isUk ? 'Гранти KPO & Тендери' : 'KPO Grants & PZP' },
    { id: 'eco_const', label: isPl ? 'EKW, BDO, KNR & Geoportal' : isUk ? 'EKW, BDO, KNR & Кадастр' : 'EKW, BDO & Cadastre' }
  ];

  const filteredAgents = agents.filter(agent => {
    const name = getLocalizedAgentName(agent, activeLang).toLowerCase();
    const desc = getLocalizedAgentDesc(agent, activeLang).toLowerCase();
    const query = searchQuery.toLowerCase();
    const matchesSearch = !query || name.includes(query) || desc.includes(query);

    if (!matchesSearch) return false;
    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'tax') return ['ksef-vat-auditor', 'cit-estonski-modeler', 'biala-lista-vat-auditor', 'ceny-transferowe-tp-generator', 'ulga-b-r-ip-box-architect', 'mdr-tax-schemes-auditor', 'windykacja-epu-odsetki-generator'].includes(agent.id);
    if (selectedCategory === 'corporate') return ['spzoo-krs-architect', 'uchwaly-krs-rdf-spzoo', 'upadlosc-restrukturyzacja-doradca'].includes(agent.id);
    if (selectedCategory === 'hr') return ['cudzoziemcy-karta-pobytu-architect', 'rodo-gdpr-dpo-compliance-auditor', 'zus-a1-delegation-specialist', 'b2b-contract-labor-auditor', 'sygnalisci-whistleblowing-auditor'].includes(agent.id);
    if (selectedCategory === 'energy_trans') return ['oze-cppa-contract-architect', 'gitd-cemt-transport-auditor'].includes(agent.id);
    if (selectedCategory === 'grants') return ['dotacje-ue-kpo-feng-architect', 'pzp-miniportal-procurement'].includes(agent.id);
    if (selectedCategory === 'eco_const') return ['ekw-rejestr-zastawow-auditor', 'bdo-waste-esg-auditor', 'construction-estimate-parser-avk', 'egib-geoportal-auditor'].includes(agent.id);
    return true;
  });

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono max-w-7xl mx-auto">
      {/* Top Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Native Language Switcher for Poland Hub */}
          <div className="flex items-center gap-1 bg-slate-950 p-1 border border-slate-700 rounded-lg">
            <button
              onClick={() => {
                setActiveLang('pl');
                if (onSelectLanguage) onSelectLanguage('pl');
                if (onSelectCurrency) onSelectCurrency('PLN');
              }}
              className={`px-2.5 py-1 text-[11px] font-bold rounded cursor-pointer transition-all ${activeLang === 'pl' ? 'bg-rose-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              🇵🇱 Polski
            </button>
            <button
              onClick={() => {
                setActiveLang('uk');
                if (onSelectLanguage) onSelectLanguage('uk');
                if (onSelectCurrency) onSelectCurrency('UAH');
              }}
              className={`px-2.5 py-1 text-[11px] font-bold rounded cursor-pointer transition-all ${activeLang === 'uk' ? 'bg-rose-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              🇺🇦 Українська
            </button>
            <button
              onClick={() => {
                setActiveLang('en');
                if (onSelectLanguage) onSelectLanguage('en');
                if (onSelectCurrency) onSelectCurrency('EUR');
              }}
              className={`px-2.5 py-1 text-[11px] font-bold rounded cursor-pointer transition-all ${activeLang === 'en' ? 'bg-rose-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              🇬🇧 English
            </button>
          </div>

          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            <span>KSeF • KRS • ZUS A1 • KNR • PZP</span>
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-rose-950/80 to-slate-900 border-2 border-rose-500/50 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-4xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-rose-500/20 border border-rose-400/40 rounded-full text-[11px] font-bold text-rose-300 uppercase tracking-wider">
            <span>🇵🇱 RZECZPOSPOLITA POLSKA • PRAWO, PODATKI & PROCEDURY</span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold uppercase tracking-tight text-white flex items-center gap-3">
            <span>{isPl ? 'CENTRUM OPERACYJNE DLA POLSKI' : isUk ? 'ПОЛЬСЬКИЙ ОПЕРАЦІЙНИЙ ЦЕНТР' : 'POLAND BUSINESS & REGULATORY HUB'}</span>
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
            {isPl
              ? 'Wyspecjalizowany ekosystem modułów analitycznych dla przedsiębiorstw działających w Polsce: walidacja faktur KSeF FA_VAT, rejestracja spółek Sp. z o.o. w KRS/S24, zaświadczenia ZUS A1, kosztorysy KNR/SEKOCENBUD, przetargi publiczne PZP oraz audyt Ksiąg Wieczystych.'
              : isUk
              ? 'Спеціалізована екосистема прикладних інструментів для бізнесу в Польщі: автоматична перевірка електронних рахунків KSeF FA_VAT, реєстрація Sp. z o.o. у KRS/S24, довідки ZUS A1, кошториси KNR/SEKOCENBUD, публічні закупівлі PZP та перевірка Ksiąg Wieczystych.'
              : 'Dedicated ecosystem of legal, tax, and procurement tools for Polish enterprises: KSeF FA_VAT validation, Sp. z o.o. KRS/S24 formation, ZUS A1 worker posting, KNR/SEKOCENBUD cost estimates, PZP public tenders, and Land Register due diligence.'}
          </p>

          {/* Quick Hub Pass Promo */}
          <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-slate-900/90 border border-rose-500/40 rounded-xl">
            <div className="space-y-1">
              <div className="text-xs font-bold text-rose-300 uppercase flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-rose-400" />
                <span>{isPl ? 'PAKIET KOMPLEKSOWY: POLAND HUB PASS' : isUk ? 'КОМПЛЕКСНИЙ ПАКЕТ: POLAND HUB PASS' : 'ALL-INCLUSIVE: POLAND HUB PASS'}</span>
              </div>
              <div className="text-[11px] text-slate-300">
                {isPl
                  ? 'Dostęp do wszystkich 7 modułów (150 zapytań/miesiąc) • BLIK / Przelewy24 / Karta'
                  : isUk
                  ? 'Повний доступ до всіх 7 модулів Польщі (150 запитів/міс) • BLIK / Картка'
                  : 'Full access to all 7 Poland modules (150 requests/month) • BLIK / Card'}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xl font-extrabold text-white font-mono">1,490 PLN<span className="text-xs text-rose-300 font-normal"> / {isPl ? 'miesiąc' : isUk ? 'міс' : 'mo'}</span></span>
              <button
                onClick={() => onLaunchAgent('ksef-vat-auditor')}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs uppercase rounded-lg transition-all shadow-md shadow-rose-900/40 cursor-pointer flex items-center gap-1.5"
              >
                <Zap className="w-3.5 h-3.5" />
                <span>{isPl ? 'Aktywuj Pakiet' : isUk ? 'Активувати Хаб' : 'Activate Pass'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white border border-slate-200 rounded-xl p-4 shadow-xs">
        {/* Search */}
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={isPl ? 'Szukaj modułu w Polsce...' : isUk ? 'Пошук польського модуля...' : 'Search Poland tool...'}
            className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-800 placeholder-slate-400 focus:outline-none focus:border-rose-500 transition-colors"
          />
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 scrollbar-none">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold font-mono uppercase whitespace-nowrap transition-all cursor-pointer ${
                selectedCategory === cat.id
                  ? 'bg-rose-600 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAgents.map(agent => {
          const name = getLocalizedAgentName(agent, activeLang);
          const desc = getLocalizedAgentDesc(agent, activeLang);
          const features = getLocalizedAgentFeatures(agent, activeLang);

          // Get pricing according to 98% margin tri-regional rule
          let pricePln = 99;
          let singlePricePln = 19;
          if (['construction-estimate-parser-avk', 'egib-geoportal-auditor', 'ceny-transferowe-tp-generator', 'upadlosc-restrukturyzacja-doradca', 'dotacje-ue-kpo-feng-architect', 'mdr-tax-schemes-auditor'].includes(agent.id)) {
            pricePln = 199;
            singlePricePln = 39;
          }

          return (
            <div
              key={agent.id}
              className="bg-white border border-slate-200 hover:border-rose-400/60 rounded-2xl p-6 shadow-xs hover:shadow-xl transition-all flex flex-col justify-between gap-5 relative group font-mono"
            >
              <div className="space-y-4">
                {/* Header Badge */}
                <div className="flex items-center justify-between gap-2">
                  <div className="w-10 h-10 rounded-xl bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 group-hover:scale-105 transition-transform">
                    <AgentIcon name={agent.icon} className="w-5 h-5" />
                  </div>
                  <div 
                    onClick={() => handleOpenPayment(agent.id, name, pricePln, singlePricePln)}
                    className="text-right cursor-pointer hover:opacity-85 transition-opacity bg-rose-50/60 hover:bg-rose-100/80 p-2 rounded-xl border border-rose-200/60"
                    title={isPl ? 'Kliknij, aby opłacić pakiet' : isUk ? 'Натисніть для вибору тарифу' : 'Click to select pricing plan'}
                  >
                    <div className="text-xs font-extrabold text-slate-900 font-mono">
                      {singlePricePln} PLN <span className="text-[10px] text-slate-500 font-normal">/ {isPl ? 'uruchomienie' : isUk ? 'запуск' : 'run'}</span>
                    </div>
                    <div className="text-[10px] text-rose-600 font-bold font-mono">
                      💳 {pricePln} PLN / 30 {isPl ? 'zapytań' : isUk ? 'запитів' : 'req'}
                    </div>
                  </div>
                </div>

                {/* Title & Desc */}
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900 uppercase tracking-tight group-hover:text-rose-600 transition-colors leading-snug">
                    {name}
                  </h3>
                  <p className="text-xs text-slate-600 leading-relaxed mt-2 line-clamp-3">
                    {desc}
                  </p>
                </div>

                {/* Feature List */}
                <ul className="space-y-1.5 text-[11px] text-slate-700 border-t border-slate-100 pt-3">
                  {features.slice(0, 3).map((f: string, i: number) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0 mt-1.5" />
                      <span className="line-clamp-1">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Buttons: Open Module + Buy Subscription */}
              <div className="pt-3 border-t border-slate-100 flex items-center gap-2">
                <button
                  onClick={() => onLaunchAgent(agent.target || agent.id, activeLang)}
                  className="flex-1 py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs uppercase rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs group-hover:shadow-md"
                >
                  <span>{isPl ? '🚀 Otwórz' : isUk ? '🚀 Відкрити' : '🚀 Open'}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleOpenPayment(agent.id, name, pricePln, singlePricePln)}
                  className="py-3 px-3.5 bg-rose-600 hover:bg-rose-700 text-white font-extrabold text-xs uppercase rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs hover:shadow-rose-600/30"
                  title={isPl ? 'Opłać subskrypcję lub pojedyncze zapytanie' : isUk ? 'Оплатити підписку або разовий запуск' : 'Buy subscription or single run'}
                >
                  <CreditCard className="w-4 h-4" />
                  <span>{isPl ? 'Kup' : isUk ? 'Оплатити' : 'Buy'}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Polish Payment Methods & Trust Section */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 text-white space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-rose-400" />
            <h4 className="text-xs sm:text-sm font-extrabold uppercase text-rose-200">
              {isPl ? '💳 Bezpieczne Płatności i Rozliczenia w Polsce' : isUk ? '💳 Безпечні Платежі та Розрахунки в Польщі' : '💳 Secure Payments in Poland'}
            </h4>
          </div>
          <button
            onClick={() => handleOpenPayment('poland-hub-pass', isPl ? 'Centrum Polski (Hub Pass)' : isUk ? 'Польський Центр (Hub Pass)' : 'Poland Hub Pass', 1490, 149)}
            className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer shadow-md"
          >
            <Sparkles className="w-4 h-4" />
            <span>{isPl ? '👑 Kup Hub Pass (1,490 PLN)' : isUk ? '👑 Купити Hub Pass (1,490 PLN)' : '👑 Buy Hub Pass (1,490 PLN)'}</span>
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
          <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center space-y-1">
            <div className="text-xs font-bold text-white">📲 BLIK</div>
            <div className="text-[10px] text-slate-400">{isPl ? 'Kod 6-cyfrowy' : isUk ? 'Миттєвий код' : '6-digit code'}</div>
          </div>
          <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center space-y-1">
            <div className="text-xs font-bold text-white">💳 Karty Płatnicze</div>
            <div className="text-[10px] text-slate-400">Visa / Mastercard</div>
          </div>
          <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center space-y-1">
            <div className="text-xs font-bold text-white">🍏 Apple & Google Pay</div>
            <div className="text-[10px] text-slate-400">{isPl ? 'Płatność 1-klik' : isUk ? 'Оплата в 1 клік' : '1-click pay'}</div>
          </div>
          <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center space-y-1">
            <div className="text-xs font-bold text-white">🏢 Faktura VAT 23%</div>
            <div className="text-[10px] text-slate-400">{isPl ? 'Dla firm (NIP)' : isUk ? 'Для юросіб (NIP)' : 'B2B Invoices'}</div>
          </div>
        </div>
      </div>

      {/* Regional Payment Modal */}
      {selectedPaymentAgent && (
        <RegionalPaymentModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          agentId={selectedPaymentAgent.id}
          agentName={selectedPaymentAgent.name}
          region="PL"
          language={activeLang}
          defaultPrice={selectedPaymentAgent.price}
          defaultSinglePrice={selectedPaymentAgent.singlePrice}
        />
      )}
    </div>
  );
}
